// Third-party
import { lazy, Suspense, useEffect, type ComponentType } from "react";

import {
  claimChunkReload,
  isChunkLoadError,
  isChunkReloadInFlight,
  isLazyModule,
  reloadForNewDeploy,
  suspendForChunkReload,
} from "./utils/chunkReload";

async function importWithRetry<T>(
  factory: () => Promise<T>,
  retries = 2,
  baseDelay = 500,
): Promise<T> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err) || attempt === retries) throw err;

      await new Promise<void>((resolve) =>
        setTimeout(resolve, baseDelay * (attempt + 1)),
      );
    }
  }
  throw new Error("unreachable");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithChunkReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    importWithRetry(factory)
      .then((mod) => {
        // Cancelling vite:preloadError stops Vite rethrowing, which resolves the
        // import to undefined rather than rejecting. A reload is already in
        // flight, so hang here instead of letting React render-throw
        // "Expected the result of a dynamic import()" on the way out.
        if (!isLazyModule(mod)) {
          if (claimChunkReload()) return reloadForNewDeploy();
          if (isChunkReloadInFlight()) return suspendForChunkReload();
          throw new TypeError("Dynamic import resolved without a default export");
        }
        return mod;
      })
      .catch((err: unknown) => {
        if (isChunkLoadError(err)) {
          if (claimChunkReload()) return reloadForNewDeploy();
          if (isChunkReloadInFlight()) return suspendForChunkReload();
        }

        throw err;
      }),
  );
}
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

// Auth & context
import AuthProvider from "./contexts/AuthProvider";
import ProtectedRoutes from "./pages/ProtectedRoutes";
import { MyCartProvider } from "./contexts/MyCartContext";
import { PriceLockProvider } from "./contexts/PriceLockContext";
import { RewardsReminderProvider } from "./contexts/RewardsReminderContext";
import FeatureErrorBoundary from "./components/common/FeatureErrorBoundary";

// Analytics
import { RouteAnalyticsTracker } from "./analytics/RouteAnalyticsTracker";
import OfflineBanner from "./components/common/OfflineBanner";
import InstallAppBanner from "./components/common/InstallAppBanner";
import ReferralUpsellPrompt from "./components/common/ReferralUpsellPrompt";

const SHOW_INSTALL_APP_BANNER = false;

// Layouts
const PublicPageLayout = lazyWithChunkReload(() => import("./components/layouts/PublicPageLayout"));
const HomeownerPageLayout = lazyWithChunkReload(() => import("./components/layouts/HomeownerPageLayout"));
const StaffPageLayout = lazyWithChunkReload(() => import("./components/layouts/StaffPageLayout"));
const PageTransition = lazyWithChunkReload(() => import("./components/layouts/PageTransition"));

// Public pages
const HomePage = lazyWithChunkReload(() => import("./pages/HomePage"));
const FAQPage = lazyWithChunkReload(() => import("./pages/FAQPage"));
const InvoicePage = lazyWithChunkReload(() => import("./pages/InvoicePage"));
const SettlePaymentPage = lazyWithChunkReload(
  () => import("./pages/SettlePaymentPage"),
);
const TermsAndConditionsPage = lazyWithChunkReload(() => import("./pages/TermsAndConditionsPage"));
const PrivacyPolicyPage = lazyWithChunkReload(() => import("./pages/PrivacyPolicyPage"));

// Auth pages
const LoginPage = lazyWithChunkReload(() => import("./pages/auth/LoginPage"));
const SignupPage = lazyWithChunkReload(() => import("./pages/auth/SignupPage"));
const ReferralLandingPage = lazyWithChunkReload(() => import("./pages/auth/ReferralLandingPage"));
const StaffLoginPage = lazyWithChunkReload(() => import("./pages/auth/StaffLoginPage"));
const ForgotPasswordPage = lazyWithChunkReload(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithChunkReload(() => import("./pages/auth/ResetPasswordPage"));
const AuthActionPage = lazyWithChunkReload(() => import("./pages/auth/AuthActionPage"));

// Service info pages
const GeneralCleaningPage = lazyWithChunkReload(() => import("./pages/services/GeneralCleaningPage"));
const AirconServicingPage = lazyWithChunkReload(() => import("./pages/services/AirconServicingPage"));
const DeepCleaningPage = lazyWithChunkReload(() => import("./pages/services/DeepCleaningPage"));
const DisinfectionPage = lazyWithChunkReload(() => import("./pages/services/DisinfectionPage"));
const OfficeCleaningPage = lazyWithChunkReload(() => import("./pages/services/OfficeCleaningPage"));

// Homeowner pages
const DashboardPage = lazyWithChunkReload(() => import("./pages/homeowner/DashboardPage"));
const MyBookingsPage = lazyWithChunkReload(() => import("./pages/homeowner/MyBookingsPage"));
const AccountPage = lazyWithChunkReload(() => import("./pages/homeowner/AccountPage"));
const MyCartPage = lazyWithChunkReload(() => import("./pages/homeowner/MyCartPage"));
const MyRewardsPage = lazyWithChunkReload(() => import("./pages/homeowner/MyRewardsPage"));
const MyPlansPage = lazyWithChunkReload(() => import("./pages/homeowner/MyPackagesPage"));

// Booking forms (multi-step)
const FormCleaningPage = lazyWithChunkReload(() => import("./pages/forms/FormCleaningPage"));
const FormAirconPage = lazyWithChunkReload(() => import("./pages/forms/FormAirconPage"));
const FormPackagePurchasePage = lazyWithChunkReload(() => import("./pages/forms/FormPackagePurchasePage"));

// Booking result pages
const BookStripeReturnPage = lazyWithChunkReload(() => import("./pages/homeowner/BookStripeReturnPage"));
const PackageStripeReturnPage = lazyWithChunkReload(() => import("./pages/homeowner/PackageStripeReturnPage"));
const BookResumePage = lazyWithChunkReload(() => import("./pages/homeowner/BookResumePage"));
const BookVerifyingPage = lazyWithChunkReload(() => import("./pages/homeowner/BookVerifyingPage"));
const BookSuccessPage = lazyWithChunkReload(() => import("./pages/homeowner/BookSuccessPage"));
const BookFailedPage = lazyWithChunkReload(() => import("./pages/homeowner/BookFailedPage"));

// Staff pages
const SchedulePage = lazyWithChunkReload(() => import("./pages/staff/SchedulePage"));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Budgets are counted in frames rather than milliseconds: requestAnimationFrame is paused
// while a tab is in the background, and a wall-clock deadline would burn down unused while
// the tab sat hidden — so a link opened in a new tab would find the budget already spent by
// the time anyone looked at it.
const HASH_SCROLL_MAX_FRAMES = 180; // ~3s at 60fps — how long to keep looking for the anchor
const HASH_SCROLL_SETTLE_FRAMES = 36; // ~0.6s — keep correcting while content above reflows

function ScrollToHash() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.replace("#", "");
    let frame = 0;
    let frames = 0;
    let sinceLastMove = 0;
    let lastTop: number | null = null;

    // Polls for the anchor instead of guessing at a fixed delay. Route components are
    // lazy-loaded, so on a cold load — an email CTA landing on /dashboard#promo-section,
    // say — the target can mount well after the hash is read, and a one-shot lookup
    // silently does nothing on a slow connection.
    const tick = () => {
      frames += 1;
      const element = document.getElementById(id);

      if (!element) {
        if (frames < HASH_SCROLL_MAX_FRAMES) frame = requestAnimationFrame(tick);
        return;
      }

      // Position within the document, not the viewport: this holds steady while the smooth
      // scroll animates, and moves only when content above the anchor reflows (images
      // loading, a lazily-rendered section appearing). Rounded so subpixel jitter does not
      // read as movement and restart the scroll every frame.
      const top = Math.round(element.getBoundingClientRect().top + window.scrollY);
      if (top !== lastTop) {
        lastTop = top;
        sinceLastMove = 0;
        element.scrollIntoView({ behavior: "smooth" });
      } else {
        sinceLastMove += 1;
      }

      if (
        sinceLastMove < HASH_SCROLL_SETTLE_FRAMES &&
        frames < HASH_SCROLL_MAX_FRAMES + HASH_SCROLL_SETTLE_FRAMES
      ) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    // Also stops a pending scroll from firing after the user has navigated on — the
    // previous setTimeout had no cleanup and could yank a freshly-mounted page.
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return null;
}

function RouteLoadingFallback() {
  return (
    <div
      aria-live="polite"
      aria-label="Loading"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #071a0e 0%, #0d2818 45%, #112e1c 75%, #071a0e 100%)",
        zIndex: 9999,
        gap: "1.4rem",
      }}
    >
      <style>{`
        @keyframes hhSpinRing {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes hhFadePulse {
          0%, 100% { opacity: 0.55; }
          50%       { opacity: 0.9; }
        }
      `}</style>

      <div
        style={{
          width: "3.1rem",
          height: "3.1rem",
          borderRadius: "50%",
          border: "3px solid rgba(44,185,106,0.2)",
          borderTopColor: "#2cb96a",
          animation: "hhSpinRing 0.8s linear infinite",
        }}
      />

      <span
        style={{
          fontFamily: "Montserrat, system-ui, sans-serif",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.55)",
          animation: "hhFadePulse 1.8s ease-in-out infinite",
        }}
      >
        HomeHelpy
      </span>
    </div>
  );
}

function StandalonePageTransition() {
  return (
    <PageTransition>
      <Outlet />
    </PageTransition>
  );
}

const SentryBrowserRouter =
  Sentry.withSentryReactRouterV6Routing(BrowserRouter);

function App() {
  return (
    <SentryBrowserRouter>
      <AuthProvider>
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => {
            const err = error as Error;
            const isChunkError = isChunkLoadError(err);
            return (
              <div style={{ padding: 24 }}>
                <h2>Something went wrong</h2>
                <p>{err?.message}</p>
                <button
                  onClick={() =>
                    isChunkError ? window.location.reload() : resetError()
                  }
                >
                  Reload
                </button>
              </div>
            );
          }}
        >
          <ScrollToTop />
          <ScrollToHash />
          <RouteAnalyticsTracker />
          <OfflineBanner />
          {/* Disabled per KAN-513 rollback — re-enable when the install prompt is ready to ship */}
          {SHOW_INSTALL_APP_BANNER && <InstallAppBanner />}
          {/* Public-page referral upsell. Path allowlist + 7-day cooldown live in
              constants/referralUpsell.ts; renders nothing until the gate opens. */}
          <ReferralUpsellPrompt />

          <PriceLockProvider>
            <RewardsReminderProvider>
              <MyCartProvider>
                <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />

                <Route element={<StandalonePageTransition />}>
                  <Route path="/staff/login" element={<StaffLoginPage />} />
                  <Route path="/auth/action" element={<AuthActionPage />} />
                </Route>

                <Route element={<PublicPageLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/ref/:code" element={<ReferralLandingPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/terms" element={<TermsAndConditionsPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="invoice/:token" element={<InvoicePage />} />
                  {/* Settle a failed subscription payment by card or PayNow (KAN-604) */}
                  <Route path="pay/:token" element={<SettlePaymentPage />} />

                  <Route path="/services">
                    <Route
                      path="classic-cleaning"
                      element={<GeneralCleaningPage />}
                    />
                    <Route
                      path="aircon-servicing"
                      element={<AirconServicingPage />}
                    />
                    <Route path="deep-cleaning" element={<DeepCleaningPage />} />
                    <Route path="disinfection" element={<DisinfectionPage />} />
                    <Route
                      path="office-cleaning"
                      element={<OfficeCleaningPage />}
                    />
                  </Route>

                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route
                    path="/book/resume"
                    element={
                      <FeatureErrorBoundary feature="book-resume" route="/book/resume">
                        <BookResumePage />
                      </FeatureErrorBoundary>
                    }
                  />
                  <Route path="/book">
                    <Route
                      path="cleaning/general-cleaning"
                      element={
                        <FeatureErrorBoundary
                          feature="booking-cleaning"
                          route="/book/cleaning/general-cleaning"
                        >
                          <FormCleaningPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="aircon/general-servicing"
                      element={
                        <FeatureErrorBoundary
                          feature="booking-aircon"
                          route="/book/aircon/general-servicing"
                        >
                          <FormAirconPage />
                        </FeatureErrorBoundary>
                      }
                    />
                  </Route>

                  <Route
                    path="/packages"
                    element={
                      <FeatureErrorBoundary feature="packages-purchase" route="/packages">
                        <FormPackagePurchasePage />
                      </FeatureErrorBoundary>
                    }
                  />
                </Route>

                <Route element={<ProtectedRoutes allowedRoles={["staff"]} />}>
                  <Route element={<StaffPageLayout />}>
                    <Route
                      path="/schedule"
                      element={
                        <FeatureErrorBoundary feature="staff-schedule" route="/schedule">
                          <SchedulePage />
                        </FeatureErrorBoundary>
                      }
                    />
                  </Route>
                </Route>

                <Route element={<ProtectedRoutes allowedRoles={["homeowner"]} />}>
                  <Route element={<HomeownerPageLayout />}>
                    <Route path="/account" element={<AccountPage />} />
                    <Route
                      path="/my-bookings"
                      element={
                        <FeatureErrorBoundary feature="my-bookings" route="/my-bookings">
                          <MyBookingsPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/my-cart"
                      element={
                        <FeatureErrorBoundary feature="my-cart" route="/my-cart">
                          <MyCartPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/my-rewards"
                      element={
                        <FeatureErrorBoundary feature="my-rewards" route="/my-rewards">
                          <MyRewardsPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/my-plans"
                      element={
                        <FeatureErrorBoundary feature="my-plans" route="/my-plans">
                          <MyPlansPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route path="/orders" element={<Navigate to="/my-bookings" replace />} />
                  </Route>

                  <Route element={<StandalonePageTransition />}>
                    <Route
                      path="/packages/return"
                      element={
                        <FeatureErrorBoundary feature="packages-return" route="/packages/return">
                          <PackageStripeReturnPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/book/verifying"
                      element={
                        <FeatureErrorBoundary feature="book-verifying" route="/book/verifying">
                          <BookVerifyingPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/book/success"
                      element={
                        <FeatureErrorBoundary feature="book-success" route="/book/success">
                          <BookSuccessPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/book/failed"
                      element={
                        <FeatureErrorBoundary feature="book-failed" route="/book/failed">
                          <BookFailedPage />
                        </FeatureErrorBoundary>
                      }
                    />
                    <Route
                      path="/book/return"
                      element={
                        <FeatureErrorBoundary feature="book-return" route="/book/return">
                          <BookStripeReturnPage />
                        </FeatureErrorBoundary>
                      }
                    />
                  </Route>
                </Route>
              </Routes>
                </Suspense>
              </MyCartProvider>
            </RewardsReminderProvider>
          </PriceLockProvider>
        </Sentry.ErrorBoundary>
      </AuthProvider>
    </SentryBrowserRouter>
  );
}

export default App;
